import React from "react";
import {createSelector, createSlice, createAsyncThunk, current} from '@reduxjs/toolkit'
import {apiCore} from "../apiSlice/apiCore";
import axios from "axios";
import Cookies from "js-cookie";
import {toast} from "react-toastify";

const uploadDocsApi = apiCore
    .injectEndpoints({
        endpoints: (build) => ({
            getUploadFileStatuses: build.query({
                providesTags: ['get-uploaded-docs-data'],
                query: () => '/docs-list/',
                transformResponse: (response) => response ?? []
            }),
            deleteDoc: build.mutation({
                query: (arg) => ({
                    url: '/remove-cheque/',
                    method: "DELETE",
                    body: {
                        docId: arg?.docId
                    }
                }),
                invalidatesTags: ['get-uploaded-docs-data'],
            })
        })
    })


export const { useUploadDocumentsMutation, useGetUploadFileStatusesQuery, useDeleteDocMutation } = uploadDocsApi;


export const uploadDocsRequest = createAsyncThunk(
    'users/uploadDocsRequest',
    async ({formData, type}, {rejectWithValue}) => {
        let uploadProgress;
        try {
            const result = await axios.post('https://accredit.kalamarket.com/api/v1/upload-doc', formData, {
                headers: {
                    Authorization: Cookies.get('primaryToken')
                },
                onUploadProgress: progressEvent => {
                    //Set the progress value to show the progress bar
                    uploadProgress = Math.round((100 * progressEvent.loaded) / progressEvent.total);
                },
            });
            if (result.data?.status === "success") {
                toast.success("فایل با موفقیت آپلود شد")
                return {data: result.data, uploadProgress: uploadProgress, type: type}
            }
        } catch (error) {
            console.log("error",error)
            toast.error(rejectWithValue(error.response.data)?.payload?.message);
        }
    }
);


const uploadDocumentSlice = createSlice({
    name: 'uploadDocumentSlice',
    initialState: {
        documents: {
            staticData: [
                {
                    title_fa: "تصویر روی کارت ملی",
                    description_fa: "کارت ملی هوشمند",
                    type: 'national_card_front',
                    uploadProgress: 0,
                },
                {
                    title_fa: "تصویر پشت کارت ملی",
                    description_fa: "کارت ملی هوشمند",
                    type: 'national_card_rear',
                    uploadProgress: 0,
                },
                {
                    title_fa: "مدارک اشتغال",
                    description_fa: "فیش حقوقی, گواهی اشتغال به کار, حکم کارگزینی, جواز کسب, پرینت گردش حساب",
                    type: 'payment_slip',
                    uploadProgress: 0,
                },
                {
                    title_fa: "تصویر شناسنامه",
                    description_fa: "صفحه اول",
                    type: 'birth',
                    uploadProgress: 0,
                },
                {
                    title_fa: "تصویر چک‌ها",
                    description_fa: "چک اقساط و چک ضمانت",
                    type: 'cheque',
                    uploadProgress: 0,
                },
                {
                    title_fa: "تصویر سند ملکی یا اجاره‌نامه",
                    description_fa: "محل سکونت اعلام شده",
                    type: 'house_document',
                    uploadProgress: 0,
                }
            ],
            apiData: [],
        },
        uploadLoading: 'idle',
        uploadError: null
    },
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(uploadDocsRequest.pending, (state, action) => {
            state.uploadLoading = 'loading'
        });
        builder.addCase(uploadDocsRequest.fulfilled, (state, action) => {
            state.uploadLoading = 'fulfilled'
            const findSelectedDocumentIndex = state.documents?.staticData?.findIndex(doc => doc.type === action.payload?.type)
            if (findSelectedDocumentIndex === -1) return;

            const newArray = [...state.documents?.staticData];
            newArray[findSelectedDocumentIndex].uploadProgress = action.payload?.uploadProgress
        });
        builder.addCase(uploadDocsRequest.rejected, (state, action) => {
            state.uploadError = 'error in upload action!'
            state.uploadLoading = 'rejected'
        })

        builder.addMatcher(uploadDocsApi.endpoints.getUploadFileStatuses.matchFulfilled, (state, {payload}) => {
            state.documents.apiData = payload;
        });
    },
})


export const useDocuments = createSelector(
    (state) => state.uploadDocumentSlice?.documents?.staticData,
    (state) => state.uploadDocumentSlice?.documents?.apiData,
    (staticData, apiData) => {
        return staticData?.map((item1) => {
            const uploadedDocs = apiData?.data?.filter((item2) => item2.type === item1.type);
            const sortedUploadedDocsArray = uploadedDocs?.sort((a, b) => {
                const statusOrder = { "ok": 1, "rejected": 2, "pend": 3 };
                return statusOrder[a.status] - statusOrder[b.status];
            });

            let okStatus = false;
            let pendStatus = false;
            let rejectedStatus = false;

            sortedUploadedDocsArray?.map(doc => {
                if (doc.status === "ok") {
                    okStatus = true;
                } else if (doc.status === "pend") {
                    pendStatus = true;
                } else if (doc.status === "rejected") {
                    rejectedStatus = true;
                }
            });

            // Set styles based on conditions
            if (rejectedStatus && okStatus && pendStatus) {
                rejectedStatus = true;
                pendStatus = false;
                okStatus = false;
            } else if(rejectedStatus && !okStatus && pendStatus){
                rejectedStatus = true;
                pendStatus = false;
                okStatus = false;
            } else if(rejectedStatus && okStatus && !pendStatus){
                rejectedStatus = true;
                pendStatus = false;
                okStatus = false;
            }else if(!rejectedStatus && okStatus && pendStatus){
                rejectedStatus = false;
                pendStatus = true;
                okStatus = false;
            }else if(!rejectedStatus && !okStatus && pendStatus){
                rejectedStatus = false;
                pendStatus = true;
                okStatus = false;
            }else if(!rejectedStatus && okStatus && !pendStatus){
                rejectedStatus = false;
                pendStatus = false;
                okStatus = true;
            }


            return { ...item1, uploadedDocs: sortedUploadedDocsArray, okStatus, pendStatus, rejectedStatus };
        });
    }
)

const arrayEquals = (arr1, arr2) => {
    if (arr1.length !== arr2.length) {
        return false;
    }

    const frequencyCounter = {};

    // Count the frequency of each element in arr1
    for (const element of arr1) {
        frequencyCounter[element] = (frequencyCounter[element] || 0) + 1;
    }

    // Decrement the frequency of each element in arr2
    for (const element of arr2) {
        if (!frequencyCounter[element]) {
            return false; // If an element is not present in arr1
        }
        frequencyCounter[element]--;
    }

    return true;
}

export const useCheckAllDocsUploaded = createSelector(
    (state) => state?.uploadDocumentSlice?.documents?.apiData,
    (state) => state?.uploadDocumentSlice?.documents?.staticData,
    (apiData,staticData) => {
        const types = staticData?.map(doc => doc.type);
        const apiDocsTypes = apiData?.data?.map(doc => doc.type);
        const uniqueTypes = [...new Set(apiDocsTypes)];
        const compareDocTypes = arrayEquals(types, uniqueTypes)
        return compareDocTypes;
    }
)

export const getDocsByType = ({type}) => createSelector(
    (state) => state.uploadDocumentSlice?.documents?.apiData,
    (apiData) => {
        return apiData?.data?.filter(doc => doc.type === type)
    }
)

export default uploadDocumentSlice.reducer